import React, { useState } from 'react';

type Variant = 'inline' | 'compact' | 'hero' | 'dark';

interface NewsletterCaptureProps {
  /** A short identifier passed as a hidden field so we can attribute leads
   *  to the page they came from in the Netlify Forms dashboard. */
  source: string;
  /** Visual variant. `inline` is the default card. `compact` is a slim
   *  one-line variant for mid-content. `hero` is large/centered. `dark`
   *  is the inverted variant for use on dark backgrounds. */
  variant?: Variant;
  /** Headline shown above the form. */
  headline?: string;
  /** Supporting copy. */
  description?: string;
  /** Submit button text. */
  ctaText?: string;
  /** Microcopy under the form. */
  microcopy?: string;
}

const DEFAULTS = {
  headline: 'Monthly insights for FS leaders',
  description:
    'Long-form analysis on AI enablement, embedded governance, and operating-model design in regulated industries. One considered essay per month, not a firehose.',
  ctaText: 'Subscribe',
  microcopy: 'No spam. Unsubscribe anytime. Read by senior practitioners across FS, healthcare, energy, and the public sector.',
};

const NewsletterCapture: React.FC<NewsletterCaptureProps> = ({
  source,
  variant = 'inline',
  headline = DEFAULTS.headline,
  description = DEFAULTS.description,
  ctaText = DEFAULTS.ctaText,
  microcopy = DEFAULTS.microcopy,
}) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    if (typeof window === 'undefined') return;

    const payload = new URLSearchParams();
    payload.append('form-name', 'newsletter');
    payload.append('email', email);
    payload.append('source', source);
    payload.append('submitted-at', new Date().toISOString());

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setSubmitted(true);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Newsletter submission failed:', err);
      }
      setSubmitError(
        'Could not subscribe just now. Please try again, or email hello@insightcentric.com.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Compact variant: one-line slim form for mid-content interruptions
  if (variant === 'compact') {
    return (
      <div className="not-prose my-10 bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 rounded-2xl p-6 md:p-8 text-white">
        {submitted ? (
          <div className="text-center py-2">
            <p className="text-base font-semibold text-white">
              Thanks — you&apos;re on the list. Next essay lands at the start of the month.
            </p>
          </div>
        ) : (
          <form
            name="newsletter"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <input type="hidden" name="form-name" value="newsletter" />
            <input type="hidden" name="source" value={source} />
            <p className="hidden">
              <label>
                Don&apos;t fill this out: <input name="bot-field" />
              </label>
            </p>
            <div>
              <p className="text-base font-semibold text-white leading-snug">
                {headline}
              </p>
              <p className="text-xs text-white/60 mt-1">One essay per month. No firehose.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                aria-label="Work email"
                className="flex-1 px-4 py-3 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent-500 bg-white placeholder:text-gray-400"
              />
              <button
                type="submit"
                disabled={submitting}
                className="bg-accent-500 hover:bg-accent-400 disabled:bg-accent-300 text-primary-900 font-bold py-3 px-6 rounded-xl transition-all whitespace-nowrap"
              >
                {submitting ? '…' : ctaText}
              </button>
            </div>
            {submitError && (
              <p role="alert" className="text-sm text-red-300">{submitError}</p>
            )}
          </form>
        )}
      </div>
    );
  }

  // ---- Dark variant: full panel on dark backgrounds (e.g. case study CTA section)
  if (variant === 'dark') {
    return (
      <div className="not-prose my-10 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-10">
        {submitted ? (
          <div className="text-center py-6">
            <h3 className="text-2xl font-bold text-white mb-2">You&apos;re on the list</h3>
            <p className="text-white/70">Next monthly essay lands in your inbox at the start of the month.</p>
          </div>
        ) : (
          <>
            <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3 leading-tight">{headline}</h3>
            <p className="text-white/70 mb-6 leading-relaxed">{description}</p>
            <form
              name="newsletter"
              method="POST"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-xl"
            >
              <input type="hidden" name="form-name" value="newsletter" />
              <input type="hidden" name="source" value={source} />
              <p className="hidden">
                <label>
                  Don&apos;t fill this out: <input name="bot-field" />
                </label>
              </p>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                aria-label="Work email"
                className="flex-1 px-5 py-4 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent-500 bg-white placeholder:text-gray-400 text-base"
              />
              <button
                type="submit"
                disabled={submitting}
                className="bg-accent-500 hover:bg-accent-400 disabled:bg-accent-300 text-primary-900 font-bold py-4 px-8 rounded-xl transition-all whitespace-nowrap"
              >
                {submitting ? 'Subscribing…' : ctaText}
              </button>
            </form>
            {submitError && (
              <p role="alert" className="text-sm text-red-300 mt-3">{submitError}</p>
            )}
            <p className="text-xs text-white/50 mt-4">{microcopy}</p>
          </>
        )}
      </div>
    );
  }

  // ---- Hero variant: large/centered for top-of-page placement
  if (variant === 'hero') {
    return (
      <section className="not-prose py-20 md:py-24 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white">
        <div className="container-custom max-w-3xl mx-auto text-center">
          {submitted ? (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">You&apos;re on the list</h2>
              <p className="text-lg text-white/80 max-w-xl mx-auto leading-relaxed">
                Next monthly essay lands in your inbox at the start of the month. We respect the inbox.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight">
                {headline}
              </h2>
              <p className="text-lg md:text-xl text-white/80 mb-10 leading-relaxed max-w-2xl mx-auto">
                {description}
              </p>
              <form
                name="newsletter"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
              >
                <input type="hidden" name="form-name" value="newsletter" />
                <input type="hidden" name="source" value={source} />
                <p className="hidden">
                  <label>
                    Don&apos;t fill this out: <input name="bot-field" />
                  </label>
                </p>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  aria-label="Work email"
                  className="flex-1 px-6 py-4 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-accent-500 bg-white placeholder:text-gray-400 text-lg"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-accent-500 hover:bg-accent-400 disabled:bg-accent-300 text-primary-900 font-bold py-4 px-10 rounded-xl transition-all whitespace-nowrap text-lg shadow-lg"
                >
                  {submitting ? 'Subscribing…' : ctaText}
                </button>
              </form>
              {submitError && (
                <p role="alert" className="text-sm text-red-200 mt-4">{submitError}</p>
              )}
              <p className="text-sm text-white/50 mt-6">{microcopy}</p>
            </>
          )}
        </div>
      </section>
    );
  }

  // ---- Default inline variant: card on light backgrounds
  return (
    <div className="not-prose my-12 bg-gradient-to-br from-accent-50/40 via-white to-white border border-accent-100 rounded-2xl p-8 md:p-10 shadow-sm">
      {submitted ? (
        <div className="text-center py-6">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-primary-900 mb-2">You&apos;re on the list</h3>
          <p className="text-gray-600">Next monthly essay lands in your inbox at the start of the month.</p>
        </div>
      ) : (
        <>
          <div className="inline-flex items-center gap-2 bg-accent-100 text-accent-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></span>
            Monthly newsletter
          </div>
          <h3 className="text-2xl md:text-3xl font-extrabold text-primary-900 mb-3 leading-tight">{headline}</h3>
          <p className="text-gray-600 mb-6 leading-relaxed">{description}</p>
          <form
            name="newsletter"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3"
          >
            <input type="hidden" name="form-name" value="newsletter" />
            <input type="hidden" name="source" value={source} />
            <p className="hidden">
              <label>
                Don&apos;t fill this out: <input name="bot-field" />
              </label>
            </p>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              aria-label="Work email"
              className="flex-1 px-5 py-4 rounded-xl border-2 border-gray-200 focus:border-accent-500 focus:ring-4 focus:ring-accent-400/30 transition-all outline-none text-base"
            />
            <button
              type="submit"
              disabled={submitting}
              className="bg-primary-900 hover:bg-primary-800 disabled:bg-primary-400 text-white font-bold py-4 px-8 rounded-xl transition-all whitespace-nowrap"
            >
              {submitting ? 'Subscribing…' : ctaText}
            </button>
          </form>
          {submitError && (
            <p role="alert" className="text-sm text-red-700 mt-3">{submitError}</p>
          )}
          <p className="text-xs text-gray-500 mt-4">{microcopy}</p>
        </>
      )}
    </div>
  );
};

export default NewsletterCapture;
